import {
  ActionIcon,
  Button,
  Divider,
  Fieldset,
  Flex,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useEffect } from 'react';
import { StepProps } from 'src/common/ModalFlow';
import ModalBox from 'src/UI/common/ModalBox';
import { NameCell } from 'src/common/components/table/NameCell';
import { Relation } from '../hooks/useFamily';
import { InProgressFamily, InProgressSecondaryMember, memberId } from './dataHelpers';
import { useFlags } from 'src/feature-flags/FlagsHook';

const relationshipOptions: { value: Relation; label: string }[] = [
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Child', label: 'Child' },
  { value: 'Other', label: 'Other' },
];

export default function DefineRelationshipsModal({
  close,
  data: family,
  setData: setFamily,
  nextStep,
  previousStep,
  canGoBack,
}: StepProps<InProgressFamily>) {
  const { flagEnabledFamilyPeerModel } = useFlags();

  useEffect(() => {
    if (!family?.mainFamilyMember || !family.secondaryFamilyMembers?.length) {
      nextStep();
    }
  }, [family, setFamily]);

  if (!family?.mainFamilyMember || !family.secondaryFamilyMembers?.length) {
    return null;
  }

  const mainClientName = [family.mainFamilyMember.firstName, family.mainFamilyMember.lastName]
    .filter(Boolean)
    .join(' ');

  const handleUpdateRelationship = (member: InProgressSecondaryMember, relationship: Relation) => {
    setFamily({
      ...family,
      secondaryFamilyMembers: family.secondaryFamilyMembers?.map(m =>
        memberId(m) === memberId(member)
          ? { ...m, relationshipToMainFamilyMember: relationship }
          : m,
      ),
    });
  };

  const relationshipText = (member: InProgressSecondaryMember) => {
    if (!member.relationshipToMainFamilyMember) {
      return 'Select relationship';
    }

    if (!family.mainFamilyMember) {
      return 'Select relationship';
    }

    return `${member.relationshipToMainFamilyMember} of ${mainClientName}`;
  };

  return (
    <Modal opened onClose={close} withCloseButton={false} centered size="xxl" variant="multi">
      <Flex gap="xxs" h="80vh" mih="80vh" direction="row" w="50vw" miw="50vw">
        <ModalBox
          title={`${family.name} Family`}
          onClose={close}
          flex={6}
          previousStep={() => previousStep({ ...family })}
          canGoBack={canGoBack}
        >
          <Flex direction="column" flex={1}>
            <Text fw={600} c="gray.10" size="md" mb="xs">
              {flagEnabledFamilyPeerModel
                ? `Please select each family member's relationship to ${mainClientName}`
                : `Please select each family member's relationship to the primary client`}
            </Text>

            <Fieldset
              legend={flagEnabledFamilyPeerModel ? mainClientName : 'Primary Client'}
              mb="xl"
              p="sm"
              fw={600}
            >
              <NameCell entity={family.mainFamilyMember} />
            </Fieldset>

            <Stack gap="md">
              {family.secondaryFamilyMembers.map((member, i) => [
                <Group key={memberId(member)} justify="space-between" align="center">
                  <NameCell entity={member} />
                  <Menu shadow="sm" width={200}>
                    <Menu.Target>
                      <Group gap="xs" style={{ cursor: 'pointer' }}>
                        <Text size="sm">{relationshipText(member)}</Text>
                        <ActionIcon variant="subtle" color="gray">
                          <IconChevronDown size={16} />
                        </ActionIcon>
                      </Group>
                    </Menu.Target>
                    <Menu.Dropdown p={0}>
                      <Text
                        size="sm"
                        fw={600}
                        px="sm"
                        py="xs"
                        style={{
                          borderBottom: 'var(--solid-border)',
                        }}
                      >
                        Relationship to {mainClientName}...
                      </Text>
                      {relationshipOptions.map(option => (
                        <Menu.Item
                          key={option.value}
                          onClick={() => {
                            handleUpdateRelationship(member, option.value);
                          }}
                          fw={600}
                          c="gray.8"
                        >
                          {option.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </Group>,
                i !== (family.secondaryFamilyMembers ?? []).length - 1 && (
                  <Divider color="gray.1" key={`divider-${i}`} />
                ),
              ])}
            </Stack>

            <Group mt="auto" justify="space-between">
              <Button variant="black" onClick={() => nextStep(family)}>
                Save
              </Button>
            </Group>
          </Flex>
        </ModalBox>
      </Flex>
    </Modal>
  );
}
